import { useSessionStore } from "@auth/sessionStore";
import { useRoomsListQuery } from "@rooms/roomsQueries";
import type { Subject } from "@subjects/subjectsClient";
import { useSubject } from "@subjects/subjectsQueries";
import { useSearch } from "@tanstack/react-router";
import { findBoardVisit } from "@visits/visitsClient";
import { useOpenVisits, useTodayVisitsQuery } from "@visits/visitsQueries";

import { resolveSelection, type WorkspaceSearch } from "./selection";

/**
 * The subject the workspace is scoped to, derived straight from the URL rather than
 * synced: it resolves the same `resolveSelection` precedence the workspace render
 * uses, so it can never disagree with what's on screen, and it updates in the same
 * render as the URL.
 *
 * Never suspends or throws — every query is a non-suspense variant gated by
 * `enabled`, so a mode needing no lookup (view, clinic) costs nothing, and a
 * loading or 404 lookup resolves to `null` rather than blowing up a consumer that
 * isn't wrapped in an AsyncBoundary.
 */
export const useActiveSubject = (): Subject | null => {
  const search = (useSearch({ from: "/", shouldThrow: false }) ??
    {}) as WorkspaceSearch;
  const selection = resolveSelection(search);
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");

  // Rules of hooks: call every query on every render, regardless of mode, and
  // let `enabled` do the gating.
  const subjectQuery = useSubject(
    selection.mode === "subject" ? selection.subjectId : null,
  );
  const visitsQuery = useTodayVisitsQuery(
    sessionKey,
    selection.mode === "visit",
  );
  // Matches WorkspaceContent's resolution (see findBoardVisit); without it the
  // pane would show an earlier day's visit while the scope resolved to null.
  const openVisitsQuery = useOpenVisits(sessionKey);
  // Enabled in visit mode too, not just room mode: the visit branch falls back
  // to the room the visit carried when the visit rolls off today's list, and
  // the rooms list is almost always already cached from the clinic grid, so
  // this is effectively free.
  const roomsQuery = useRoomsListQuery(
    sessionKey,
    true,
    selection.mode === "room" || selection.mode === "visit",
  );

  switch (selection.mode) {
    // A view overlay scopes recordings and subject-scoped panels clinic-wide,
    // not to the room/visit hidden underneath it, so nothing binds to a
    // subject the staff can't see.
    case "view":
      return null;

    case "subject":
      return subjectQuery.data ?? null;

    case "visit": {
      const visit = findBoardVisit(
        selection.visitUuid,
        visitsQuery.data,
        openVisitsQuery.data,
      );
      if (visit)
        return {
          id: visit.patientId,
          name: visit.patientName,
          kind: "patient",
        };
      // Neither board read has arrived yet: no scope rather than the carried
      // room, which would scope recordings and panels to an unrelated room for a
      // request cycle before snapping to the patient.
      if (visitsQuery.isPending || openVisitsQuery.isPending) return null;
      // The visit id matches neither board read (day rollover, cancellation,
      // older than the still-open window). This mirrors WorkspaceContent's
      // fall-through to the room pane: resolve the room the visit carried so the
      // scope tracks the pane that actually shows on screen. If the room misses
      // too (or the carried roomUuid is null), there's nothing to scope to.
      const room = selection.roomUuid
        ? roomsQuery.data?.find((r) => r.id === selection.roomUuid)
        : undefined;
      return room ? { id: room.id, name: room.name, kind: "room" } : null;
    }

    case "room": {
      // `active: true` matches WorkspaceContent's `useRoomsList(sessionKey,
      // true)` so this reads the exact same cache entry rather than
      // triggering a second fetch.
      const room = roomsQuery.data?.find((r) => r.id === selection.roomUuid);
      return room ? { id: room.id, name: room.name, kind: "room" } : null;
    }

    case "clinic":
      return null;
  }
};
